import { useState, useEffect, useCallback, Fragment } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, ChevronRight, Trash2, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { pillStyle } from '@/components/StatusDot';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';

const AGENCIES = ['DOB', 'DOT', 'FDNY', 'Other'];
const RESULT_STATUSES = ['Scheduled', 'Passed', 'Failed', 'Re-inspection Required'];
const RESULT_COLORS = {
  'Scheduled': '#031333',           // neutral
  'Passed': '#16A34A',              // green
  'Failed': '#E5441A',              // scarlet
  'Re-inspection Required': '#D97706', // amber
};

const fmtDate = (iso) => {
  if (!iso) return '\u00A0';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '\u00A0' : d.toLocaleDateString();
};

function ResultPill({ status }) {
  return <span className="inline-flex items-center" style={pillStyle(RESULT_COLORS[status] || '#71717A')} data-testid="inspection-result-pill">{status || 'Scheduled'}</span>;
}

const EMPTY_ADD = {
  agency: 'DOB', inspection_type: '', scheduled_date: '', completed_date: '',
  result_status: 'Scheduled', inspector_name: '', notes: '', follow_up_action: '',
};

export function InspectionsTab({ projectId, onUpdated }) {
  const confirm = useConfirm();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchInspections = useCallback(() => {
    return api.get(`/inspections?project_id=${projectId}`)
      .then(r => setInspections(Array.isArray(r.data) ? r.data : []))
      .catch(() => setInspections([]));
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    fetchInspections().finally(() => setLoading(false));
  }, [fetchInspections]);

  const rows = Array.isArray(inspections) ? inspections : [];

  const openAdd = () => { setAddForm(EMPTY_ADD); setAddOpen(true); };

  const createInspection = async () => {
    if (!addForm.inspection_type.trim()) return toast.error('Inspection Type is required');
    setSaving(true);
    try {
      const r = await api.post('/inspections', {
        project_id: projectId,
        agency: addForm.agency,
        inspection_type: addForm.inspection_type.trim(),
        scheduled_date: addForm.scheduled_date || null,
        completed_date: addForm.completed_date || null,
        result_status: addForm.result_status,
        inspector_name: addForm.inspector_name || null,
        notes: addForm.notes,
        follow_up_action: addForm.follow_up_action || null,
      });
      if (['Failed', 'Re-inspection Required'].includes(r.data?.result_status)) {
        toast.success('Inspection added, follow-up to-do created');
      } else {
        toast.success('Inspection added');
      }
      setAddOpen(false);
      setAddForm(EMPTY_ADD);
      fetchInspections();
      if (onUpdated) onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding inspection'); }
    setSaving(false);
  };

  const toggleRow = (i) => {
    if (expandedId === i.id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(i.id);
    setDetail({ ...i });
  };

  const saveDetail = async (patch) => {
    if (!detail) return;
    const prevStatus = detail.result_status;
    setDetail(prev => ({ ...prev, ...patch }));
    try {
      const r = await api.put(`/inspections/${detail.id}`, patch);
      setDetail(r.data);
      setInspections(prev => prev.map(i => (i.id === r.data.id ? r.data : i)));
      if (patch.result_status && ['Failed', 'Re-inspection Required'].includes(patch.result_status) && !['Failed', 'Re-inspection Required'].includes(prevStatus)) {
        toast.success('Follow-up to-do created');
        if (onUpdated) onUpdated();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); fetchInspections(); }
  };

  const deleteInspection = async (id) => {
    const ok = await confirm({ title: 'Delete inspection', description: 'Delete this inspection? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/inspections/${id}`);
      toast.success('Inspection deleted');
      setExpandedId(null);
      setDetail(null);
      fetchInspections();
      if (onUpdated) onUpdated();
    } catch { toast.error('Could not delete inspection'); }
  };

  const renderDetailPanel = () => {
    if (!detail) return null;
    const followUpRelevant = ['Failed', 'Re-inspection Required'].includes(detail.result_status);
    return (
      <div className="space-y-4" data-testid={`inspection-detail-${detail.id}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Agency</Label>
            <Select value={detail.agency || 'Other'} onValueChange={v => saveDetail({ agency: v })}>
              <SelectTrigger className="bg-background border-border mt-1" data-testid="inspection-detail-agency"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{AGENCIES.map(a => <SelectItem key={a} value={a} className="text-sm">{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Result Status</Label>
            <Select value={detail.result_status || 'Scheduled'} onValueChange={v => saveDetail({ result_status: v })}>
              <SelectTrigger className="bg-background border-border mt-1" data-testid="inspection-detail-result-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{RESULT_STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Inspection Type</Label>
          <Input value={detail.inspection_type || ''} onChange={e => setDetail({ ...detail, inspection_type: e.target.value })} onBlur={e => saveDetail({ inspection_type: e.target.value })} className="bg-background border-border mt-1" data-testid="inspection-detail-type" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Scheduled Date</Label>
            <Input type="date" value={detail.scheduled_date || ''} onChange={e => saveDetail({ scheduled_date: e.target.value || null })} className="bg-background border-border mt-1" data-testid="inspection-detail-scheduled" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Completed Date</Label>
            <Input type="date" value={detail.completed_date || ''} onChange={e => saveDetail({ completed_date: e.target.value || null })} className="bg-background border-border mt-1" data-testid="inspection-detail-completed" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Inspector Name</Label>
          <Input value={detail.inspector_name || ''} onChange={e => setDetail({ ...detail, inspector_name: e.target.value })} onBlur={e => saveDetail({ inspector_name: e.target.value || null })} className="bg-background border-border mt-1" data-testid="inspection-detail-inspector" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Notes</Label>
          <Textarea value={detail.notes || ''} onChange={e => setDetail({ ...detail, notes: e.target.value })} onBlur={e => saveDetail({ notes: e.target.value })} rows={2} className="bg-background border-border mt-1" data-testid="inspection-detail-notes" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Follow-up Action {followUpRelevant && <span className="text-[#E5441A]">(needed)</span>}</Label>
          <Textarea value={detail.follow_up_action || ''} onChange={e => setDetail({ ...detail, follow_up_action: e.target.value })} onBlur={e => saveDetail({ follow_up_action: e.target.value || null })} rows={2} placeholder="What needs to happen if Failed / Re-inspection Required" className="bg-background border-border mt-1" data-testid="inspection-detail-followup" />
        </div>
        <div className="flex justify-end">
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-border" onClick={() => deleteInspection(detail.id)} data-testid="inspection-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>
      </div>
    );
  };

  if (loading) return <p className="text-sm text-muted-foreground py-4">Loading inspections...</p>;

  return (
    <div className="space-y-3" data-testid="inspections-tab">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{rows.length} inspection{rows.length === 1 ? '' : 's'} logged</p>
        <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={openAdd} data-testid="add-inspection-btn"><Plus className="h-4 w-4 mr-1" /> Add Inspection</Button>
      </div>

      {rows.length === 0 ? (
        <div className="py-8 text-center space-y-2" data-testid="inspections-empty">
          <ClipboardList className="h-6 w-6 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No inspections logged for this project.</p>
        </div>
      ) : (
        <div className="border border-border rounded-md bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs text-muted-foreground">Agency</TableHead>
                <TableHead className="text-xs text-muted-foreground">Inspection Type</TableHead>
                <TableHead className="text-xs text-muted-foreground">Scheduled</TableHead>
                <TableHead className="text-xs text-muted-foreground">Completed</TableHead>
                <TableHead className="text-xs text-muted-foreground">Result</TableHead>
                <TableHead className="text-xs text-muted-foreground">Inspector</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(i => (
                <Fragment key={i.id}>
                  <TableRow
                    className={`group border-border cursor-pointer ${expandedId === i.id ? 'bg-secondary/40' : 'hover:bg-secondary/30'}`}
                    onClick={() => toggleRow(i)}
                    data-testid={`inspection-row-${i.id}`}
                    style={{ height: '40px' }}
                  >
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5" style={{ color: '#010A21' }}>
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform" style={{ transform: expandedId === i.id ? 'rotate(90deg)' : 'none' }} />
                        {i.agency}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm" style={{ color: '#010A21' }}>{i.inspection_type || '\u00A0'}</TableCell>
                    <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtDate(i.scheduled_date)}</TableCell>
                    <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtDate(i.completed_date)}</TableCell>
                    <TableCell><ResultPill status={i.result_status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{i.inspector_name || '\u00A0'}</TableCell>
                  </TableRow>
                  <tr data-testid={`inspection-expand-${i.id}`}>
                    <td colSpan={6} style={{ padding: 0, border: 0 }}>
                      <div style={{ maxHeight: expandedId === i.id ? '2000px' : '0', overflow: 'hidden', transition: 'max-height 200ms ease-out' }}>
                        {expandedId === i.id && (
                          <div className="px-4 py-4 bg-secondary/20 border-b border-border">
                            {renderDetailPanel()}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Inspection slide-out */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="bg-card border-border w-full sm:max-w-md overflow-y-auto" data-testid="inspection-add-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: '#010A21' }}>Add Inspection</SheetTitle>
            <SheetDescription>Log a scheduled or completed inspection for this project.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Agency</Label>
                <Select value={addForm.agency} onValueChange={v => setAddForm({ ...addForm, agency: v })}>
                  <SelectTrigger className="bg-background border-border mt-1" data-testid="inspection-add-agency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">{AGENCIES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Result Status</Label>
                <Select value={addForm.result_status} onValueChange={v => setAddForm({ ...addForm, result_status: v })}>
                  <SelectTrigger className="bg-background border-border mt-1" data-testid="inspection-add-result-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">{RESULT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Inspection Type *</Label>
              <Input value={addForm.inspection_type} onChange={e => setAddForm({ ...addForm, inspection_type: e.target.value })} placeholder="e.g. Footing Inspection" className="bg-background border-border mt-1" data-testid="inspection-add-type" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Scheduled Date</Label>
                <Input type="date" value={addForm.scheduled_date} onChange={e => setAddForm({ ...addForm, scheduled_date: e.target.value })} className="bg-background border-border mt-1" data-testid="inspection-add-scheduled" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Completed Date <span className="text-muted-foreground/60">(optional)</span></Label>
                <Input type="date" value={addForm.completed_date} onChange={e => setAddForm({ ...addForm, completed_date: e.target.value })} className="bg-background border-border mt-1" data-testid="inspection-add-completed" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Inspector Name <span className="text-muted-foreground/60">(optional)</span></Label>
              <Input value={addForm.inspector_name} onChange={e => setAddForm({ ...addForm, inspector_name: e.target.value })} className="bg-background border-border mt-1" data-testid="inspection-add-inspector" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Textarea value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} rows={2} className="bg-background border-border mt-1" data-testid="inspection-add-notes" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Follow-up Action <span className="text-muted-foreground/60">(optional)</span></Label>
              <Textarea value={addForm.follow_up_action} onChange={e => setAddForm({ ...addForm, follow_up_action: e.target.value })} rows={2} placeholder="Relevant when Failed / Re-inspection Required" className="bg-background border-border mt-1" data-testid="inspection-add-followup" />
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90" disabled={saving} onClick={createInspection} data-testid="inspection-add-submit">
              {saving ? 'Adding...' : 'Add Inspection'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default InspectionsTab;
